import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ImageNg from '@fec/frontend/elements/media/image-ng/ImageNg';
import PlayIcon from '@fec/frontend/elements/icons/play-icon/PlayIcon';
import MediaTimeIndicator from '@fec/frontend/elements/media/media-time-indicator/MediaTimeIndicator';
import BasicCaption from '@fec/frontend-aron/elements/media/BasicCaption/BasicCaption';
import {
  MEDIA_CLICKED,
  MEDIA_PLAYING,
  triggerEvent,
} from '@fec/assets/js/utils/event';
import { ResponsiveHelper } from '@fec/frontend/foundation/client/responsive-helper';
import usePlayerIsBlocked from '@fec/frontend-aron/foundation/hooks/usePlayerIsBlocked';
import usePlayerAppEvents from '@fec/frontend-aron/foundation/hooks/usePlayerAppEvents';
import usePlayerA11yHelper from '@fec/frontend-aron/foundation/hooks/usePlayerA11yHelper';

import './VideoGalleryItem.scss';

const VideoGalleryItem = ({
  asset,
  frontendId,
  isWebview,
  setActiveVideoGalleryItem,
  activeVideoGalleryItem,
}) => {
  const { urn, image, duration, bridgePayload } = asset;
  const videoGalleryItemRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const {
    dispatchMediaClickedEvents,
    dispatchMediaStoppedEvents,
    hasActivePlayer,
  } = usePlayerAppEvents(urn, bridgePayload);
  const { isBlocked } = usePlayerIsBlocked(asset);
  const { mainLabelWithoutPlayerState } = usePlayerA11yHelper(
    asset,
    isBlocked,
    false,
  );

  const handleItemClick = () => {
    if (!isActive) {
      setActiveVideoGalleryItem({
        urn,
        frontendId,
        title: asset.title,
      });
    }

    if (isWebview) {
      if (isActive) {
        dispatchMediaStoppedEvents();
      } else {
        dispatchMediaClickedEvents();
      }
    } else {
      triggerEvent(MEDIA_CLICKED, { urn, frontendId, title: asset.title });
    }
  };

  useEffect(() => {
    if (activeVideoGalleryItem.frontendId === frontendId) {
      // scroll the active VideoGalleryItem into view.
      // needed for the auto-continue feature as well as partially visible items on mobile
      videoGalleryItemRef.current.scrollIntoView({
        inline: ResponsiveHelper.isMobile() ? 'center' : 'nearest',
        block: 'nearest',
        behavior: 'smooth',
      });
      setIsActive(true);

      if (activeVideoGalleryItem.isFocused) {
        videoGalleryItemRef.current.focus();
      }
    } else {
      setIsActive(false);
    }
  }, [activeVideoGalleryItem]);

  useEffect(() => {
    if (isWebview) {
      if (hasActivePlayer) {
        setActiveVideoGalleryItem({
          urn,
          frontendId,
          title: asset.title,
        });
      } else {
        setIsActive(false);
      }
    }
  }, [hasActivePlayer, isWebview]);

  useEffect(() => {
    const handlePlayingEvent = (event) => {
      if (event.detail.frontendId === frontendId) {
        setActiveVideoGalleryItem({
          urn,
          frontendId,
          title: asset.title,
        });
      }
    };

    window.addEventListener(MEDIA_PLAYING, handlePlayingEvent);
    return () => window.removeEventListener(MEDIA_PLAYING, handlePlayingEvent);
  }, [frontendId]);

  return (
    <li
      className={classNames('video-gallery-item', {
        'video-gallery-item--active': isActive,
      })}>
      <button
        ref={videoGalleryItemRef}
        className="video-gallery-item__button"
        aria-label={mainLabelWithoutPlayerState}
        onClick={handleItemClick}>
        <div className="video-gallery-item__image-container" aria-hidden="true">
          <div className="video-gallery-item__image">
            <ImageNg image={image} />
          </div>
          {!isActive && (
            <>
              <div
                className="video-gallery-item__play-button"
                aria-hidden="true">
                <PlayIcon type="video" />
              </div>
              <div className="video-gallery-item__duration" aria-hidden="true">
                <MediaTimeIndicator urn={urn} duration={duration} />
              </div>
            </>
          )}
        </div>
        <div className="video-gallery-item__caption" aria-hidden="true">
          <BasicCaption
            asset={asset}
            isActive={isActive}
            fillContainer={true}
          />
        </div>
      </button>
    </li>
  );
};

VideoGalleryItem.propTypes = {
  asset: PropTypes.object,
  frontendId: PropTypes.string,
  isWebview: PropTypes.bool,
  activeVideoGalleryItem: PropTypes.object.isRequired,
  setActiveVideoGalleryItem: PropTypes.func.isRequired,
};

export default VideoGalleryItem;
